/**
 * Keeping a session alive without keeping a token anywhere a script can read
 * it (S2-05, docs/design/tokens.md §2.8).
 *
 * The access token lives in memory (lib/auth/store.ts) for 900 seconds. What
 * carries the session across reloads and renewals is the refresh cookie the
 * API sets on sign-in: HttpOnly, so page script never sees it, and scoped to
 * /v1/auth. POST /v1/auth/refresh spends it and answers a new access token
 * and a new cookie. Each refresh token works ONCE: presenting a spent one is
 * treated as theft and ends the whole session (401 SESSION_REVOKED).
 *
 * That last rule decides most of this file. Two refreshes that carry the same
 * cookie end the session, so:
 *
 * - within a tab, concurrent callers share one refresh (single flight);
 * - across tabs, the refresh runs inside the Web Lock `atlas-session-refresh`.
 *   The browser reads the cookie jar when a request is SENT, so a tab that
 *   waited for the lock sends the cookie the previous holder was just given,
 *   never the spent one. Where Web Locks are missing, single flight still
 *   holds within the tab;
 * - a tab that renews tells the others (BroadcastChannel `atlas-session`)
 *   and hands over the token, so the tabs share one chain and one refresh
 *   per cycle rather than one per tab. Signing out is announced the same way.
 *
 * When it happens:
 * - on load, restoreSession(): once, before the first render;
 * - before expiry, a timer (exp - iat - 60 s after a token arrives; both
 *   figures are the server's, so the browser's clock decides nothing);
 * - after a 401 TOKEN_EXPIRED or TOKEN_INVALID, once, from the API client
 *   (lib/api/client.ts), which then sends the request again (renewFor).
 *
 * A refusal (NO_SESSION, SESSION_REVOKED, SESSION_EXPIRED) ends the session
 * with the existing "session ended" notice; on load, NO_SESSION is simply
 * "not signed in". A network or server failure keeps the session: the next
 * call, or the timer 30 seconds later, tries again.
 *
 * 401 MFA_REQUIRED (W2.8b) is the server pausing the refresh: a second factor
 * became required of this person here (a privileged grant since sign-in, or
 * the workspace now requires it of everyone) and none is set up. This screen
 * cannot set one up without a session, so retrying would never succeed: it
 * used to be treated as "unavailable", which on load left a Try again that
 * could not work (D1002). The paused session is ended on the server (POST
 * /v1/auth/logout, so it is not left live and listed) and here, with a
 * notice; signing in again then routes the person to enrolment in place.
 */
import { post, toApiError, type ApiError } from "@/lib/api/client";
import { SESSION_MESSAGES } from "@/lib/auth/messages";
import { useAuthStore, type AtlasClaims } from "@/lib/auth/store";

/** The Web Lock every tab of this origin takes to refresh. */
export const REFRESH_LOCK = "atlas-session-refresh";
/** The channel tabs use to share renewals and sign-outs. */
export const SESSION_CHANNEL = "atlas-session";
/** Where earlier builds persisted the session, bearer token included. */
export const LEGACY_STORAGE_KEY = "atlas-auth";
/** Renew this long before the token's own lifetime runs out. */
export const RENEW_LEAD_SECONDS = 60;
/** After a refresh that could not reach the server, try again this soon. */
export const RENEW_RETRY_MS = 30_000;
/**
 * A refresh is never abandoned quickly: the server may already have rotated
 * the cookie, and a response the browser never reads leaves it holding the
 * spent one (the next refresh would then look like theft).
 */
export const REFRESH_TIMEOUT_MS = 30_000;

/** The refusals that mean "this session is over", not "try later". */
const SESSION_OVER = new Set([
  "NO_SESSION",
  "SESSION_REVOKED",
  "SESSION_EXPIRED",
]);

/** The refresh paused until a second factor is set up (W2.8b, D1002). */
const MFA_REQUIRED = "MFA_REQUIRED";

/** How long ending a paused session waits for the server (as sign-out). */
const END_PAUSED_TIMEOUT_MS = 5_000;

interface RefreshBody {
  accessToken: string;
  claims: AtlasClaims;
  activeTenantCode: string;
}

export type RefreshOutcome =
  /** This tab holds a fresh token (from the server, or from another tab). */
  | { kind: "renewed" }
  /** The server says the session is over; this tab is signed out. */
  | { kind: "ended"; code: string }
  /** The server could not be reached or failed; the session is kept. */
  | { kind: "unavailable"; error: ApiError }
  /** This tab signed in, out or switched while the refresh ran; ignored. */
  | { kind: "superseded" };

// ---------------------------------------------------------------- refresh --

let inflight: Promise<RefreshOutcome> | null = null;

/**
 * Exchange the refresh cookie for a new access token. Concurrent callers in
 * this tab share one call; other tabs wait for the lock.
 */
export function refreshSession(): Promise<RefreshOutcome> {
  if (!inflight) {
    const asked = useAuthStore.getState().accessToken;
    inflight = withRefreshLock(() => exchange(asked)).finally(() => {
      inflight = null;
    });
  }
  return inflight;
}

async function withRefreshLock<T>(work: () => Promise<T>): Promise<T> {
  const locks = lockManager();
  if (!locks) return work();
  return locks.request(REFRESH_LOCK, { mode: "exclusive" }, work);
}

function lockManager(): LockManager | undefined {
  try {
    const locks = globalThis.navigator?.locks;
    return typeof locks?.request === "function" ? locks : undefined;
  } catch {
    return undefined;
  }
}

/**
 * One exchange, run while holding the lock. `asked` is the token this tab
 * held when the refresh was asked for (null when restoring): the answer is
 * applied only to that same state, never over a sign-out, a new sign-in or
 * a token another tab handed over in the meantime.
 */
async function exchange(asked: string | null): Promise<RefreshOutcome> {
  const before = useAuthStore.getState();
  if (before.accessToken !== asked) {
    // Another tab renewed while this one waited for the lock, and handed the
    // token over: nothing to fetch, and fetching would rotate again.
    return asked !== null && sameSession(asked, before.accessToken)
      ? { kind: "renewed" }
      : { kind: "superseded" };
  }
  if (asked === null && before.sessionStatus !== "restoring") {
    return { kind: "superseded" };
  }

  let body: RefreshBody;
  try {
    body = await post<RefreshBody>("/auth/refresh", {}, undefined, {
      timeout: REFRESH_TIMEOUT_MS,
    });
  } catch (err) {
    const error = toApiError(err);
    const now = useAuthStore.getState();
    if (now.accessToken !== asked) return { kind: "superseded" };
    if (error.status === 401 && error.code === MFA_REQUIRED) {
      // Sign-in, not this session, is the way on: it routes to enrolment.
      const sid = now.claims?.sid ?? null;
      try {
        await post<unknown>("/auth/logout", undefined, undefined, {
          timeout: END_PAUSED_TIMEOUT_MS,
        });
      } catch {
        // Ignored, as in signOut: the local end below does not depend on it.
      }
      if (useAuthStore.getState().accessToken !== asked) {
        return { kind: "superseded" };
      }
      useAuthStore.getState().endSession(SESSION_MESSAGES.mfaRequired);
      announceSignedOut(sid);
      return { kind: "ended", code: error.code };
    }
    if (error.status === 401 && SESSION_OVER.has(error.code)) {
      // On load, "no session" is not news; anything else (revoked, expired,
      // a reused token) is, mid-session or on load.
      if (asked === null && error.code === "NO_SESSION") now.logout();
      else now.endSession(SESSION_MESSAGES.sessionEnded);
      return { kind: "ended", code: error.code };
    }
    if (asked === null) now.restoreFailed(error);
    return { kind: "unavailable", error };
  }

  const now = useAuthStore.getState();
  if (now.accessToken !== asked) return { kind: "superseded" };
  if (asked === null) {
    if (now.sessionStatus !== "restoring") return { kind: "superseded" };
    now.setSession(body.accessToken, body.claims, body.activeTenantCode);
  } else if (now.claims?.sub !== body.claims.sub) {
    // The cookie now names someone else: another person signed in on this
    // browser. This tab's own session can no longer be renewed, and it must
    // not quietly become theirs.
    now.endSession(SESSION_MESSAGES.sessionEnded);
    return { kind: "ended", code: "SESSION_REPLACED" };
  } else if (now.claims?.sid === body.claims.sid) {
    now.renewSession(body.accessToken, body.claims);
  } else {
    // The same person signed in again in another tab; the cookie is that
    // session's now, so this tab follows it.
    now.setSession(body.accessToken, body.claims, body.activeTenantCode);
  }
  announce({ type: "renewed", ...body });
  return { kind: "renewed" };
}

/**
 * On load: ask the server whether this browser has a session. Protected
 * screens wait while the status is "restoring" (App.tsx RequireAuth).
 */
export async function restoreSession(): Promise<RefreshOutcome> {
  const store = useAuthStore.getState();
  if (store.accessToken) return { kind: "renewed" };
  store.beginRestore();
  return refreshSession();
}

/**
 * Called by the API client after a 401 TOKEN_EXPIRED / TOKEN_INVALID on a
 * request that carried `sent`. Renews once if `sent` is still this tab's
 * token, and answers whether the request may be sent again: only with a
 * newer token of the SAME session. A request made under one session is
 * never replayed under another (a different person, or a new sign-in).
 */
export async function renewFor(sent: string): Promise<boolean> {
  if (useAuthStore.getState().accessToken === sent) await refreshSession();
  const now = useAuthStore.getState();
  if (!now.accessToken || now.accessToken === sent) return false;
  return sameSession(sent, now.accessToken);
}

function sameSession(a: string, b: string | null): boolean {
  if (!b) return false;
  const sid = tokenSid(a);
  return sid !== null && sid === tokenSid(b);
}

/** The `sid` claim of a token, read without verifying (the server does). */
export function tokenSid(token: string): string | null {
  const payload = token.split(".")[1];
  if (!payload) return null;
  try {
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    const claims = JSON.parse(atob(padded)) as { sid?: unknown };
    return typeof claims.sid === "string" && claims.sid ? claims.sid : null;
  } catch {
    return null;
  }
}

// -------------------------------------------------------------- renewal --

let renewTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Renew `exp - iat - 60` seconds after this token arrived (half its life for
 * a token shorter than two minutes). Only a token that names a session can
 * be renewed: one issued before a workspace exists has no refresh (§2.10).
 * Background tabs whose timers are throttled fall back to refresh-and-retry.
 */
export function scheduleRenewal(): void {
  if (renewTimer) clearTimeout(renewTimer);
  renewTimer = null;
  const { accessToken, claims } = useAuthStore.getState();
  if (!accessToken || !claims?.sid) return;
  const lifetime = claims.exp - claims.iat;
  if (!Number.isFinite(lifetime) || lifetime <= 0) return;
  const lead = Math.min(RENEW_LEAD_SECONDS, lifetime / 2);
  renewTimer = setTimeout(
    () => void renewOnTime(accessToken),
    (lifetime - lead) * 1000,
  );
}

async function renewOnTime(token: string): Promise<void> {
  renewTimer = null;
  if (useAuthStore.getState().accessToken !== token) return;
  const outcome = await refreshSession();
  if (
    outcome.kind === "unavailable" &&
    useAuthStore.getState().accessToken === token
  ) {
    renewTimer = setTimeout(() => void renewOnTime(token), RENEW_RETRY_MS);
  }
}

// ------------------------------------------------------------ other tabs --

type SessionMessage =
  | ({ type: "renewed" } & RefreshBody)
  | { type: "signed-out"; sid: string | null };

let channel: BroadcastChannel | null = null;
let stopSync: (() => void) | null = null;

function announce(message: SessionMessage): void {
  try {
    channel?.postMessage(message);
  } catch {
    // A tab that cannot tell the others still works on its own: they renew
    // through the lock and learn of a sign-out on their next request.
  }
}

/**
 * Sign-out in this tab ends the same session in the others at once, with
 * the "session ended" notice (lib/auth/sign-out.ts). Without it they would
 * learn on their next request, from 401 SESSION_REVOKED.
 */
export function announceSignedOut(sid: string | null): void {
  announce({ type: "signed-out", sid });
}

/** What another tab said, applied to this one. Exported for tests. */
export function applySessionMessage(data: unknown): void {
  if (!data || typeof data !== "object") return;
  const message = data as Partial<SessionMessage>;
  const state = useAuthStore.getState();

  if (message.type === "signed-out") {
    if (state.sessionStatus !== "signedIn") return;
    const sid = (message as { sid?: unknown }).sid;
    if (typeof sid === "string" && sid !== state.claims?.sid) return;
    state.endSession(SESSION_MESSAGES.sessionEnded);
    return;
  }

  if (message.type !== "renewed") return;
  const { accessToken, claims, activeTenantCode } =
    message as Partial<RefreshBody>;
  if (
    typeof accessToken !== "string" ||
    !claims ||
    typeof claims.sub !== "string" ||
    typeof claims.sid !== "string" ||
    typeof activeTenantCode !== "string"
  ) {
    return;
  }
  if (state.sessionStatus === "restoring") {
    // This tab was about to ask for the same thing.
    state.setSession(accessToken, claims, activeTenantCode);
    return;
  }
  if (state.sessionStatus !== "signedIn" || !state.claims) return;
  // Someone else's session is never adopted: this tab ends at its own next
  // refresh, when the cookie turns out to be theirs.
  if (claims.sub !== state.claims.sub) return;
  if (claims.sid === state.claims.sid) {
    // Messages can cross: never go back to an older token.
    if (claims.iat < state.claims.iat) return;
    state.renewSession(accessToken, claims);
  } else {
    state.setSession(accessToken, claims, activeTenantCode);
  }
}

/**
 * Start the session machinery for this page: the renewal timer (rescheduled
 * whenever the token changes) and the channel to the other tabs. Called once
 * from main.tsx; returns the function that stops it.
 */
export function startSessionSync(): () => void {
  if (stopSync) return stopSync;
  if (typeof BroadcastChannel === "function") {
    try {
      channel = new BroadcastChannel(SESSION_CHANNEL);
      channel.onmessage = (event: MessageEvent) =>
        applySessionMessage(event.data);
    } catch {
      channel = null;
    }
  }
  const unsubscribe = useAuthStore.subscribe((state, prev) => {
    if (state.accessToken !== prev.accessToken) scheduleRenewal();
  });
  scheduleRenewal();
  stopSync = () => {
    unsubscribe();
    if (renewTimer) clearTimeout(renewTimer);
    renewTimer = null;
    channel?.close();
    channel = null;
    stopSync = null;
  };
  return stopSync;
}

// ----------------------------------------------------------------- legacy --

/**
 * Delete the session earlier builds persisted, bearer token included, from
 * every browser that ever signed in. Runs before the first render.
 */
export function removeLegacyToken(): void {
  try {
    globalThis.localStorage?.removeItem(LEGACY_STORAGE_KEY);
  } catch {
    // Storage blocked (private mode, a sandbox): there is nothing to delete.
  }
}
